import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios'; // Use default axios import
import { useNotificationStore } from '@/stores/notifications';
import { API_URL } from '@/config';
import storage from '@/utils/storage';

export const refreshAccessToken = async (refresh_token: string): Promise<any> => {
  try {
    // console.log(
    //   'Making refresh token API call with token:',
    //   refresh_token.substring(0, 20) + '...'
    // );
    const response = await axios.post(`${API_URL}/v2/auth/refresh?refresh_token=${refresh_token}`);
    // console.log('Refresh token API response:', response.data);
    return response;
  } catch (error: any) {
    console.error('Error refreshing access token:', error);

    // If the API returns 401, it means the refresh token is invalid/expired
    if (error?.response?.status === 401) {
      // Clear all tokens and redirect to login
      storage.clearAllTokens();
      window.location.assign(window.location.origin as unknown as string);
    }

    throw error;
  }
};

export const useRefreshAccessToken = ({ config } = {} as { config?: any }) => {
  const { addNotification } = useNotificationStore();
  const queryClient = useQueryClient();

  return useMutation<any, Error, string>({
    mutationFn: refreshAccessToken,
    onError: (error: any) => {
      console.error('Token refresh failed:', error);

      // Handle 401 errors specifically - refresh token is invalid
      if (error?.response?.status === 401) {
        addNotification({
          type: 'error',
          title: 'Session expired',
          message: 'Please log in again',
        });
        // Clear tokens and redirect - this is also handled in the refreshAccessToken function
        // but adding here as a fallback
        storage.clearAllTokens();
        window.location.assign(window.location.origin as unknown as string);
      } else {
        addNotification({
          type: 'error',
          title: 'Failed to refresh token',
          message: error.message,
        });
      }
    },
    onSuccess: (data) => {
      // console.log('Token refresh onSuccess called with data:', data);

      // Store new tokens if present in response
      if (data?.data?.tokens) {
        // console.log('Found tokens in data.data.tokens');
        const { access_token, id_token } = data.data.tokens;

        if (access_token) {
          // console.log('Storing new access token');
          storage.setAccessToken(access_token);
        }

        if (id_token) {
          // console.log('Storing new id token');
          storage.setIdToken(id_token);
        }
      } else if (data?.tokens) {
        // console.log('Found tokens in data.tokens');
        // Alternative response structure
        const { access_token, id_token } = data.tokens;

        if (access_token) {
          // console.log('Storing new access token (alternative structure)');
          storage.setAccessToken(access_token);
        }

        if (id_token) {
          // console.log('Storing new id token (alternative structure)');
          storage.setIdToken(id_token);
        }
      } else {
        console.warn('No tokens found in refresh response:', data);
        console.warn('Response structure:', JSON.stringify(data, null, 2));
      }

      // Invalidate teams queries to refetch updated data
      queryClient.invalidateQueries('teams');
      queryClient.invalidateQueries(['team']);
      queryClient.invalidateQueries(['flowscaleUser']);
    },
    ...config,
  });
};
